import { DestroyRef, Injectable, inject } from '@angular/core';
import { TRACKING_NODES, TrackingNode } from './tracking-nodes';

export const TRACKING_ELEMENTS = ['login_submit', 'logout', 'impressum', 'privacy', 'case_details', 'chat_send', 'chat_action', 'chat_restart', 'quick_payment', 'quick_document_upload', 'quick_contact', 'wizard_back', 'wizard_close', 'wizard_confirm_close', 'payment_method', 'payment_submit', 'contract_download', 'master_data_close', 'master_data_submit', 'privacy_download', 'contact_phone', 'contact_email', 'contact_website'] as const;
export type TrackingFlow = 'payment' | 'rzv' | 'rate_pause' | 'master_data';
type TrackingPage = 'login' | 'portal';
type TrackingError = 'network' | 'validation' | 'unexpected';
type TrackingEvent = 'chat_node' | 'page_view' | 'click' | 'login_start' | 'login_submit' | 'login_success' | 'login_abort' | 'chat_open' | 'chat_message_sent' | 'flow_start' | 'flow_submit' | 'flow_complete' | 'flow_abort' | 'page_leave' | 'visibility' | 'error';
interface QueuedEvent { ts: number; event: TrackingEvent; page: TrackingPage; element: string | null; meta: Record<string, string | number>; }
interface Attempt { id: string; submitted: boolean; }
const SESSION_KEY = 'wakayama_tracking_session_id';
const PAYPAL_KEY = 'wakayama_tracking_paypal_flow_id';
const PAYPAL_POPUP_KEY = 'wakayama_tracking_paypal_popup';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Best-effort, bounded first-party telemetry. It never receives case IDs or user content. */
@Injectable({ providedIn: 'root' })
export class TrackingService {
  private readonly destroyRef = inject(DestroyRef);
  private sessionId = '';
  private lastChatNode: TrackingNode | null = null;
  private page: TrackingPage | null = null;
  private visitId = '';
  private queue: QueuedEvent[] = [];
  private initialized = false;
  private left = false;
  private visibleSince: number | null = null;
  private visibleMs = 0;
  private loginAttempt: Attempt | null = null;
  private flows: Partial<Record<TrackingFlow, Attempt>> = {};
  private paypalRedirecting = false;
  private sending = false;
  private paymentChannel: BroadcastChannel | null = null;
  private remotePaymentId = '';
  private remoteSubmitted = false;

  init(): void {
    if (this.initialized || this.excluded()) return;
    this.initialized = true;
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      this.sessionId = stored && UUID.test(stored) ? stored : this.uuid();
      try { sessionStorage.setItem(SESSION_KEY, this.sessionId); } catch { /* Tab-memory fallback. */ }
    } catch { this.sessionId = this.uuid(); }
    if (!this.sessionId) return;
    try {
      this.paymentChannel = new BroadcastChannel('wakayama_tracking_payment');
      this.paymentChannel.onmessage = ({ data }: MessageEvent) => {
        if (!data || typeof data !== 'object' || Array.isArray(data)
          || Object.keys(data).some(key => !['flow_id', 'event', 'code'].includes(key))
          || !this.flows.payment || typeof data.flow_id !== 'string' || !UUID.test(data.flow_id)
          || data.flow_id !== this.flows.payment.id || this.remotePaymentId) return;
        if (data.event === 'submit' && data.code === undefined) this.submitFlow('payment');
        else if (data.event === 'complete' && data.code === undefined) this.completeFlow('payment');
        else if (data.event === 'abort' && data.code === undefined) this.abortFlow('payment');
        else if (data.event === 'error' && ['network', 'validation', 'unexpected'].includes(data.code)) this.error(data.code, 'payment');
      };
    } catch { /* Popup result observation is best effort when this native API is unavailable. */ }
    const click = (event: Event) => {
      const element = event.target instanceof Element ? event.target.closest('[data-track]') : null;
      const code = element?.getAttribute('data-track');
      if (code && TRACKING_ELEMENTS.includes(code as typeof TRACKING_ELEMENTS[number]) && !element?.closest('[data-track-private]')) {
        this.emit('click', {}, code);
      }
    };
    const visibility = () => {
      this.snapshot();
      if (document.visibilityState === 'hidden') {
        this.emit('visibility', { state: 'hidden', visible_ms: this.duration() });
        this.flush(true);
      } else if (!this.left) {
        this.visibleSince = performance.now();
        this.emit('visibility', { state: 'visible', visible_ms: this.duration() });
      }
    };
    const hide = () => { this.leave(true); this.flush(true); };
    const show = () => { if (this.left && this.page) this.setPage(this.page, true); };
    document.addEventListener('click', click);
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', show);
    const interval = window.setInterval(() => this.flush(), 10000);
    this.destroyRef.onDestroy(() => {
      window.clearInterval(interval);
      this.paymentChannel?.close();
      document.removeEventListener('click', click);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', show);
    });
  }

  setPage(page: TrackingPage, force = false): void {
    this.init();
    if (!this.sessionId || this.excluded() || (this.page === page && !force)) return;
    if (this.page && !this.left) this.leave();
    if (this.page !== page) this.resetChatContext();
    this.page = page;
    this.visitId = this.uuid();
    this.left = false;
    this.visibleMs = 0;
    this.visibleSince = document.visibilityState === 'hidden' ? null : performance.now();
    this.emit('page_view');
  }

  beginLogin(): void {
    if (this.page !== 'login' || this.loginAttempt) return;
    this.loginAttempt = { id: this.uuid(), submitted: false };
    this.emit('login_start', { flow_id: this.loginAttempt.id });
  }
  submitLogin(): void {
    this.beginLogin();
    if (this.loginAttempt && !this.loginAttempt.submitted) {
      this.loginAttempt.submitted = true;
      this.emit('login_submit', { flow_id: this.loginAttempt.id });
    }
  }
  loginSuccess(): void {
    if (!this.loginAttempt?.submitted) return;
    this.emit('login_success', { flow_id: this.loginAttempt.id });
    this.loginAttempt = null;
  }
  resetChatContext(): void { this.lastChatNode = null; }

  chatNode(node: TrackingNode): void {
    try {
      if (this.page !== 'portal' || typeof node !== 'string' || !Object.hasOwn(TRACKING_NODES, node) || node === this.lastChatNode) return;
      this.emit('chat_node', { node_id: node });
      this.lastChatNode = node;
    } catch { /* Telemetry must never interrupt a bot response. */ }
  }

  chatOpen(): void { this.emit('chat_open'); }
  chatMessage(): void { this.emit('chat_message_sent'); }

  startFlow(flow: TrackingFlow): void {
    if (this.flows[flow]) return;
    this.flows[flow] = { id: this.uuid(), submitted: false };
    this.emit('flow_start', { flow, flow_id: this.flows[flow]!.id });
  }
  submitFlow(flow: TrackingFlow): void {
    if (flow === 'payment' && this.remotePaymentId) {
      if (!this.remoteSubmitted) { this.remoteSubmitted = true; this.notifyPayment('submit'); }
      return;
    }
    const attempt = this.flows[flow];
    if (!attempt || attempt.submitted) return;
    attempt.submitted = true;
    this.emit('flow_submit', { flow, flow_id: attempt.id });
  }
  completeFlow(flow: TrackingFlow): void {
    if (flow === 'payment' && this.remotePaymentId) {
      if (this.remoteSubmitted) { this.notifyPayment('complete'); this.clearPaypal(); }
      return;
    }
    const attempt = this.flows[flow];
    if (!attempt?.submitted) return;
    this.emit('flow_complete', { flow, flow_id: attempt.id });
    delete this.flows[flow];
    if (flow === 'payment') this.clearPaypal();
  }
  abortFlow(flow: TrackingFlow): void {
    if (flow === 'payment' && this.remotePaymentId) { this.notifyPayment('abort'); this.clearPaypal(); return; }
    const attempt = this.flows[flow];
    if (!attempt) return;
    this.emit('flow_abort', { flow, flow_id: attempt.id });
    delete this.flows[flow];
    if (flow === 'payment') this.clearPaypal();
  }
  error(code: TrackingError, flow?: TrackingFlow): void {
    if (flow === 'payment' && this.remotePaymentId) { this.notifyPayment('error', code); return; }
    const attempt = flow ? this.flows[flow] : null;
    this.emit('error', { code, ...(flow && attempt ? { flow, flow_id: attempt.id } : {}) });
  }

  /** The only cross-navigation flow is PayPal. Persist only its own random ID. */
  paypalRedirect(popup?: Window): void {
    this.startFlow('payment');
    this.paypalRedirecting = true;
    try { sessionStorage.setItem(PAYPAL_KEY, this.flows.payment!.id); } catch { /* Best effort. */ }
    if (popup) {
      try {
        // A popup starts with a cloned sessionStorage. Give it an independent tab ID.
        popup.sessionStorage.setItem(SESSION_KEY, this.uuid());
        popup.sessionStorage.setItem(PAYPAL_KEY, this.flows.payment!.id);
        popup.sessionStorage.setItem(PAYPAL_POPUP_KEY, '1');
      } catch { /* A blocked popup storage must never block the payment. */ }
    }
  }
  paypalReturn(): void {
    try {
      const id = sessionStorage.getItem(PAYPAL_KEY);
      if (id && UUID.test(id)) {
        if (sessionStorage.getItem(PAYPAL_POPUP_KEY) === '1') this.remotePaymentId = id;
        else this.flows.payment = { id, submitted: false };
      }
    } catch { /* No cross-navigation measurement without storage. */ }
    this.paypalRedirecting = false;
  }
  private notifyPayment(event: 'submit' | 'complete' | 'abort' | 'error', code?: TrackingError): void {
    try { this.paymentChannel?.postMessage({ flow_id: this.remotePaymentId, event, ...(code ? { code } : {}) }); } catch { /* Best effort. */ }
  }
  private clearPaypal(): void {
    this.remotePaymentId = '';
    this.remoteSubmitted = false;
    this.paypalRedirecting = false;
    try { sessionStorage.removeItem(PAYPAL_KEY); sessionStorage.removeItem(PAYPAL_POPUP_KEY); } catch { /* Best effort. */ }
  }

  private excluded(): boolean { return /^\/tracking(?:\/|$)/.test(window.location.pathname); }
  private uuid(): string {
    try { return crypto.randomUUID(); } catch { return ''; }
  }
  private emit(event: TrackingEvent, meta: Record<string, string | number> = {}, element: string | null = null): void {
    if (!this.page || !this.sessionId || !this.visitId || this.excluded()) return;
    // No retries or growing backlog when telemetry fails.
    if (this.queue.length >= 100) this.queue.shift();
    this.queue.push({ ts: Date.now(), event, page: this.page, element, meta: { visit_id: this.visitId, ...meta } });
  }
  private snapshot(): void {
    if (this.visibleSince !== null) {
      this.visibleMs += Math.max(0, performance.now() - this.visibleSince);
      this.visibleSince = null;
    }
  }
  private duration(): number { return Math.min(86400000, Math.max(0, Math.round(this.visibleMs))); }
  private leave(preservePaypal = false): void {
    if (this.left || !this.page) return;
    this.snapshot();
    if (this.loginAttempt) {
      this.emit('login_abort', { flow_id: this.loginAttempt.id });
      this.loginAttempt = null;
    }
    for (const flow of ['payment', 'rzv', 'rate_pause', 'master_data'] as const) {
      if (flow !== 'payment' || !preservePaypal || !this.paypalRedirecting) this.abortFlow(flow);
    }
    this.emit('page_leave', { visible_ms: this.duration() });
    this.left = true;
  }

  flush(beacon = false): void {
    if (!this.queue.length || this.excluded() || (!beacon && this.sending)) return;
    let remaining = 2; // At most the bounded 100-event backlog, no retry loop.
    while (this.queue.length && remaining-- > 0) {
      let count = Math.min(50, this.queue.length);
      let blob: Blob;
      do {
        blob = new Blob([JSON.stringify({ session_id: this.sessionId, events: this.queue.slice(0, count) })], { type: 'application/json' });
        if (blob.size <= 32768) break;
        count--;
      } while (count > 0);
      if (!count) { this.queue.shift(); continue; }
      try {
        if (beacon && typeof navigator.sendBeacon === 'function' && navigator.sendBeacon('/api/tracking/events', blob)) {
          this.queue.splice(0, count);
          continue;
        }
      } catch { /* One bounded fetch fallback below. */ }
      if (this.sending) return;
      let timer: number | undefined;
      try {
        const controller = new AbortController();
        timer = window.setTimeout(() => controller.abort(), 5000);
        this.sending = true;
        const request = fetch('/api/tracking/events', { method: 'POST', body: blob, credentials: 'same-origin', keepalive: beacon, signal: controller.signal });
        this.queue.splice(0, count);
        void request.catch(() => undefined).finally(() => { window.clearTimeout(timer); this.sending = false; });
      } catch { window.clearTimeout(timer); this.sending = false; this.queue.splice(0, count); }
      if (!beacon) break;
    }
  }
}
